import { useHistory } from "react-router-dom";
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle } from "@ionic/react";
import { LogOut, User, Repeat, ChevronRight } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useStore } from "../store/useStore";
import { useTheme } from "../hooks/useTheme";
import { isNativeShell } from "../lib/native/nativeShell";
import { releaseNativePushOnSignOut } from "../lib/native/nativePush";
import ThemeSelector from "../components/settings/ThemeSelector";
import TagManager from "../components/settings/TagManager";
import ConnectedAppsPanel from "../components/settings/ConnectedAppsPanel";
import SlackConnectionPanel from "../components/settings/SlackConnectionPanel";
import NudgesPanel from "../components/settings/NudgesPanel";

export default function SettingsPage() {
  const history = useHistory();
  const userProfile = useStore((state) => state.userProfile);
  const tags = useStore((state) => state.tags);
  const createTag = useStore((state) => state.createTag);
  const updateTag = useStore((state) => state.updateTag);
  const deleteTag = useStore((state) => state.deleteTag);
  const { theme, setTheme } = useTheme();

  const handleSignOut = async () => {
    // Unsubscribe while the session is still valid so the server stops pushing to this device.
    if (isNativeShell()) await releaseNativePushOnSignOut();
    await supabase.auth.signOut();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Settings</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-4 space-y-6">
          {/* Profile section */}
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              {userProfile?.profile_photo_url ? (
                <img
                  src={userProfile.profile_photo_url}
                  alt={userProfile.name}
                  className="w-12 h-12 rounded-full"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
              )}
              <div>
                <p className="font-medium text-foreground">
                  {userProfile?.name || "User"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {userProfile?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Appearance */}
          <ThemeSelector theme={theme} setTheme={setTheme} />

          {/* Tags */}
          <TagManager
            tags={tags}
            createTag={createTag}
            updateTag={updateTag}
            deleteTag={deleteTag}
          />

          {/* Navigation links */}
          <div className="bg-surface border border-border rounded-lg divide-y divide-border">
            <button
              onClick={() => history.push("/recurring")}
              className="w-full flex items-center justify-between p-4 hover:bg-surface-raised transition-colors"
            >
              <div className="flex items-center gap-3">
                <Repeat className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  Recurring Tasks
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Nudges */}
          <NudgesPanel />

          {/* Slack Integration */}
          <SlackConnectionPanel />

          {/* Connected AI apps (OAuth grants) */}
          <ConnectedAppsPanel />

          {/* Sign out */}
          <button
            onClick={handleSignOut}
            className="btn-danger w-full flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </IonContent>
    </IonPage>
  );
}
